import { z } from 'zod';

/**
 * M5 — Cuestionarios. El corazón del producto.
 *
 * ── El requisito duro ──
 * Un cuestionario respondido **no se edita**: editarlo crea una versión nueva, y
 * cada respuesta apunta a la versión que su autor vio. Sin eso, corregir una tilde
 * invalida en silencio el «antes vs. después», que es lo que Copathy vende — los
 * números siguen saliendo, solo que comparan cosas distintas.
 *
 * Lo hace cumplir el **motor** (`tg_*_solo_borrador`, migración 011). Lo de acá es
 * el contrato; no es lo que protege.
 */

/**
 * Los diez tipos. Nueve se responden; la sección organiza.
 *
 * Los cuatro primeros llegaron con la 011; los cinco de texto y dato suelto con
 * la 015, cuando se confirmó la duda #11 — y la confirmó también el antecedente:
 * **46 de 274 respuestas en AppSheet fueron texto libre**, el segundo tipo más
 * usado (`docs/10-appsheet-antecedente.md`).
 *
 * ⚠️ **No hay tipo «0 a 10»**: eso es una `likert` con la escala «Del 0 al 10»,
 * que la 015 sembró con once puntos. Un tipo aparte sería otra rama en cada
 * `switch` para dibujar exactamente lo mismo.
 *
 * ⚠️ **No hay foto ni video**, y no es un olvido: no son un tipo de pregunta más,
 * son almacenamiento de archivos por respuesta. Y una foto que sube un trabajador
 * es un dato personal que no se puede anonimizar ni agregar. Necesita decisión
 * escrita antes que una columna.
 */
export const TIPOS_PREGUNTA = [
  'seccion',
  'likert',
  'matriz_likert',
  'opcion_unica',
  'opcion_multiple',
  'texto_corto',
  'texto_largo',
  'numero',
  'si_no',
  'fecha',
] as const;
export type TipoPregunta = (typeof TIPOS_PREGUNTA)[number];

/** Cómo se agrupan en el selector. Diez botones sueltos no se eligen, se sufren. */
export const FAMILIAS_PREGUNTA = [
  { clave: 'escala', titulo: 'Medir' },
  { clave: 'eleccion', titulo: 'Elegir' },
  { clave: 'abierta', titulo: 'Escribir' },
  { clave: 'estructura', titulo: 'Organizar' },
] as const;
export type FamiliaPregunta = (typeof FAMILIAS_PREGUNTA)[number]['clave'];

/**
 * La paleta de acentos de una sección. **Cerrada a propósito**: un selector de
 * color libre produce formularios con doce colores peleando entre sí y contra la
 * marca, y un hex libre viaja hasta el CSS de la app móvil sin que nadie lo
 * valide. El nombre se elige acá; **el tono exacto lo decide cada pantalla** —la
 * web con sus variables, la app móvil con las suyas.
 */
export const COLORES_SECCION = [
  'marino',
  'naranja',
  'verde',
  'morado',
  'turquesa',
  'rosa',
] as const;
export type ColorSeccion = (typeof COLORES_SECCION)[number];

export const TIPO_PREGUNTA: Record<
  TipoPregunta,
  { titulo: string; ayuda: string; familia: FamiliaPregunta }
> = {
  likert: {
    titulo: 'Escala',
    ayuda: 'Una afirmación y una escala. Es el tipo principal.',
    familia: 'escala',
  },
  matriz_likert: {
    titulo: 'Matriz de escala',
    ayuda:
      'Varias afirmaciones con la misma escala, en una sola pantalla. Sin esto, quince afirmaciones son quince pantallas y la gente abandona.',
    familia: 'escala',
  },
  opcion_unica: {
    titulo: 'Selección única',
    ayuda: 'Una sola respuesta entre varias.',
    familia: 'eleccion',
  },
  opcion_multiple: {
    titulo: 'Selección múltiple',
    ayuda: 'Varias respuestas, con un mínimo y un máximo.',
    familia: 'eleccion',
  },
  si_no: {
    titulo: 'Sí o no',
    ayuda: 'Dos botones. Es una selección única, pero sin teclear las opciones cada vez.',
    familia: 'eleccion',
  },
  texto_corto: {
    titulo: 'Texto corto',
    ayuda: 'Una línea: un nombre, una palabra, una idea suelta.',
    familia: 'abierta',
  },
  texto_largo: {
    titulo: 'Texto libre',
    ayuda: 'Varios renglones. Es donde aparece lo que ninguna opción anticipó.',
    familia: 'abierta',
  },
  numero: {
    titulo: 'Número',
    ayuda: 'Una cifra, con mínimo y máximo opcionales.',
    familia: 'abierta',
  },
  fecha: { titulo: 'Fecha', ayuda: 'Un día del calendario.', familia: 'abierta' },
  seccion: {
    titulo: 'Sección',
    ayuda:
      'Un título con subtítulo que abre una página nueva, con su propio color. Es lo que convierte treinta preguntas en un recorrido por partes.',
    familia: 'estructura',
  },
};

/** Qué tipos usan una escala Likert, y cuáles usan ítems. Una sola definición. */
export const usaEscala = (tipo: TipoPregunta) => tipo === 'likert' || tipo === 'matriz_likert';
export const usaItems = (tipo: TipoPregunta) =>
  tipo === 'opcion_unica' || tipo === 'opcion_multiple' || tipo === 'matriz_likert';

/**
 * `minimo` y `maximo` significan **cosas distintas según el tipo**, y por eso hay
 * una función que lo dice en vez de un `if` repartido por la aplicación:
 * en `opcion_multiple` son cuántas opciones se pueden marcar; en `numero`, el
 * rango del valor. En los demás no se usan.
 */
export const usaRango = (tipo: TipoPregunta) =>
  tipo === 'opcion_multiple' || tipo === 'numero';

/** Una sección no se responde: organiza. Todo lo que cuenta preguntas la salta. */
export const esSeccion = (tipo: TipoPregunta) => tipo === 'seccion';

export const ESTADOS_VERSION = ['borrador', 'publicada', 'retirada'] as const;
export type EstadoVersion = (typeof ESTADOS_VERSION)[number];

// ---------------------------------------------------------------------------
// Escalas
// ---------------------------------------------------------------------------

export const esquemaPuntoEscala = z.object({
  id: z.number().int().positive(),
  orden: z.number().int().positive(),
  etiqueta: z.string(),
  /** La carita. En AppSheet la Likert de Copathy SE RESPONDÍA con caritas. */
  emoji: z.string().nullable(),
});

export const esquemaEscala = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
  activo: z.boolean(),
  puntos: z.array(esquemaPuntoEscala),
  /** Cuántas preguntas de versiones ya publicadas la usan. Si hay alguna, no se edita. */
  usosPublicados: z.number().int(),
});
export type Escala = z.infer<typeof esquemaEscala>;

export const esquemaCrearEscala = z.object({
  nombre: z.string().trim().min(3, 'Ponle un nombre reconocible.').max(120),
  /**
   * Las etiquetas, en orden. **Mínimo dos**: una escala de un punto no mide nada.
   * El tope de 11 cubre hasta la de 0–10.
   */
  puntos: z
    .array(
      z.object({
        etiqueta: z.string().trim().min(1, 'Cada punto necesita su etiqueta.').max(120),
        /*
          Hasta 16 caracteres: un emoji compuesto (tono de piel, bandera) llega a
          8–10. No se valida que SEA un emoji — distinguirlo de texto es una
          guerra perdida contra Unicode; lo que importa es que no quepa un párrafo.
        */
        emoji: z.string().trim().min(1).max(16).nullable().optional(),
      }),
    )
    .min(2, 'Una escala necesita al menos dos puntos.')
    .max(11, 'Más de once puntos deja de leerse como una escala.'),
});
export type EntradaCrearEscala = z.infer<typeof esquemaCrearEscala>;

// ---------------------------------------------------------------------------
// Cuestionarios
// ---------------------------------------------------------------------------

export const esquemaItemPregunta = z.object({
  id: z.number().int().positive(),
  orden: z.number().int().positive(),
  texto: z.string(),
});

export const esquemaPregunta = z.object({
  id: z.number().int().positive(),
  orden: z.number().int().positive(),
  tipo: z.enum(TIPOS_PREGUNTA),
  texto: z.string(),
  ayuda: z.string().nullable(),
  obligatoria: z.boolean(),
  competenciaId: z.number().int().nullable(),
  competencia: z.string().nullable(),
  escalaId: z.number().int().nullable(),
  minimo: z.number().int().nullable(),
  maximo: z.number().int().nullable(),
  /** El acento de una sección. NULL en todo lo demás. */
  color: z.string().nullable(),
  /** Decorativo, para cualquier tipo: acompaña al enunciado o al título. */
  emoji: z.string().nullable(),
  /** Opciones de una pregunta de selección, o afirmaciones de una matriz. */
  items: z.array(esquemaItemPregunta),
});
export type Pregunta = z.infer<typeof esquemaPregunta>;

export const esquemaVersion = z.object({
  id: z.number().int().positive(),
  version: z.number().int().positive(),
  estado: z.enum(ESTADOS_VERSION),
  publicadaEn: z.string().nullable(),
  preguntas: z.array(esquemaPregunta),
});
export type Version = z.infer<typeof esquemaVersion>;

export const esquemaCuestionario = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
  descripcion: z.string().nullable(),
  activo: z.boolean(),
  /** Número de la versión publicada, si hay. */
  versionPublicada: z.number().int().nullable(),
  /** `true` si hay una versión en borrador esperando. */
  tieneBorrador: z.boolean(),
  preguntas: z.number().int(),
  creadoEn: z.string(),
});
export type Cuestionario = z.infer<typeof esquemaCuestionario>;

export const esquemaCrearCuestionario = z.object({
  nombre: z.string().trim().min(3, 'Ponle un nombre reconocible.').max(160),
  descripcion: z.string().trim().max(1000).optional(),
});
export type EntradaCrearCuestionario = z.infer<typeof esquemaCrearCuestionario>;

export const esquemaEditarCuestionario = z
  .object({
    nombre: z.string().trim().min(3).max(160).optional(),
    descripcion: z.string().trim().max(1000).nullable().optional(),
    activo: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'No enviaste ningún cambio.');
export type EntradaEditarCuestionario = z.infer<typeof esquemaEditarCuestionario>;

/**
 * Una pregunta del borrador.
 *
 * ⚠️ Se manda **la pregunta completa, con sus ítems**, y no una lista de
 * operaciones sueltas. Con «agregar opción» y «quitar opción» separados, dos
 * personas editando a la vez producen una mezcla que ninguna pidió; con el estado
 * final, la última gana — que es un resultado que alguien eligió.
 */
export const esquemaGuardarPregunta = z
  .object({
    tipo: z.enum(TIPOS_PREGUNTA),
    texto: z.string().trim().min(3, 'Escribe la pregunta.').max(500),
    ayuda: z.string().trim().max(500).nullable().optional(),
    obligatoria: z.boolean().default(true),
    competenciaId: z.number().int().positive().nullable().optional(),
    escalaId: z.number().int().positive().nullable().optional(),
    minimo: z.number().int().min(0).nullable().optional(),
    maximo: z.number().int().positive().nullable().optional(),
    color: z.enum(COLORES_SECCION).nullable().optional(),
    emoji: z.string().trim().min(1).max(16).nullable().optional(),
    /** Opciones o afirmaciones, en orden. */
    items: z.array(z.string().trim().min(1).max(300)).max(30).default([]),
  })
  .superRefine((v, ctx) => {
    /*
      Las mismas reglas que el motor, comprobadas antes de mandar. No es una
      duplicación gratuita: el CHECK de la base responde con un mensaje de
      PostgreSQL, y acá se puede decir cuál campo falta.
    */
    if (usaEscala(v.tipo) && !v.escalaId) {
      ctx.addIssue({ code: 'custom', path: ['escalaId'], message: 'Elige una escala.' });
    }
    if (!usaEscala(v.tipo) && v.escalaId) {
      ctx.addIssue({
        code: 'custom',
        path: ['escalaId'],
        message: 'Este tipo de pregunta no usa escala.',
      });
    }
    if (usaItems(v.tipo) && v.items.length < 2) {
      ctx.addIssue({
        code: 'custom',
        path: ['items'],
        message:
          v.tipo === 'matriz_likert'
            ? 'Una matriz necesita al menos dos afirmaciones.'
            : 'Una pregunta de selección necesita al menos dos opciones.',
      });
    }
    if (v.minimo != null && v.maximo != null && v.minimo > v.maximo) {
      ctx.addIssue({ code: 'custom', path: ['maximo'], message: 'El máximo no puede ser menor.' });
    }
    /*
      El tope solo se compara contra los ítems en las de selección. En una
      pregunta de `numero`, `maximo` es el valor más alto que se acepta —«¿cuántos
      años llevas?», máximo 60— y no tiene nada que ver con cuántas opciones hay.
    */
    if (v.maximo != null && usaItems(v.tipo) && v.maximo > v.items.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['maximo'],
        message: 'No se pueden pedir más opciones de las que hay.',
      });
    }
    if (!usaRango(v.tipo) && (v.minimo != null || v.maximo != null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['minimo'],
        message: 'Este tipo de pregunta no usa mínimo ni máximo.',
      });
    }
    if (v.color != null && !esSeccion(v.tipo)) {
      ctx.addIssue({
        code: 'custom',
        path: ['color'],
        message: 'El color es de las secciones.',
      });
    }
  });
export type EntradaGuardarPregunta = z.infer<typeof esquemaGuardarPregunta>;

/** Reordenar: la lista completa de ids en el orden nuevo. */
export const esquemaReordenar = z.object({
  preguntas: z.array(z.number().int().positive()).min(1).max(300),
});
export type EntradaReordenar = z.infer<typeof esquemaReordenar>;
